/**
 * SaveV1. Principle: serialize rules inputs, never physics outputs — no
 * velocities, no behaviors, no steering state. A loaded world re-disperses
 * deterministically and looks alive instantly.
 *
 * DOM-free (shared by worker, main, and tests).
 */
import type { RngState } from '../core/rng';

export const SAVE_VERSION = 1;

export interface SavedCreature {
  x: number;
  z: number;
  seed: number;
  phase: number;
  scaleQ: number;
  genes?: number[];
  hunger: number;
  clean: number;
  fun: number;
  rest: number;
  lifeState: number;
  lowMoodSec: number;
  ageSec: number;
  splitCharge: number;
  splitCooldown: number;
  circadian: number;
  speedMul: number;
  fam?: number; // familiarity — personal bond with the player
}

export interface SavedItem {
  type: number;
  state: number;
  x: number;
  z: number;
}

export interface SaveV1 {
  version: 1;
  seed: string;
  createdAt: number; // epoch ms
  savedAt: number; // epoch ms
  simTick: number;
  simTimeSec: number;
  rng: {
    steer: RngState;
    spawn: RngState;
    needs: RngState;
    mitosis: RngState;
  };
  creatures: SavedCreature[];
  items: SavedItem[];
  onboarding: {
    hatched: boolean;
    fed: boolean;
    cleaned: boolean;
    played: boolean;
  };
  meta?: {
    pm: unknown; // PlayerModelState — owned by the worker
    stage: number; // language ladder ratchet
    discovered: string[]; // logbook entry ids — owned by main
  };
}

/** Hand validator — no schema lib; returns null if the shape is unusable. */
export function validateSave(raw: unknown): SaveV1 | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const s = raw as Partial<SaveV1>;
  if (s.version !== 1) return null;
  if (typeof s.seed !== 'string') return null;
  if (typeof s.savedAt !== 'number' || typeof s.simTick !== 'number') return null;
  if (!Array.isArray(s.creatures) || !Array.isArray(s.items)) return null;
  if (typeof s.rng !== 'object' || s.rng === null) return null;
  return raw as SaveV1;
}

export interface AwayReport {
  elapsedH: number;
  splits: number;
  wentDormant: number;
  popBefore: number;
  popAfter: number;
  moodAfter: number; // 0..255 average
}
